import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, History, GitCompareArrows, Plus, Bell, Loader2,
  PlusCircle, MinusCircle, AlertTriangle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const WRITE_ROLES = ["owner", "admin", "project_manager", "superintendent"];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ProjectRevisions() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [project, setProject] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOld, setSelectedOld] = useState(null);
  const [selectedNew, setSelectedNew] = useState(null);
  const [compare, setCompare] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [notifyingId, setNotifyingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, h] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/revisions`),
      ]);
      setProject(p.data);
      setHistory(h.data || []);
      if ((h.data || []).length >= 2) {
        setSelectedNew(h.data[0].id);
        setSelectedOld(h.data[1].id);
      } else if ((h.data || []).length === 1) {
        setSelectedNew(h.data[0].id);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load revisions");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // Run compare whenever selection changes.
  useEffect(() => {
    if (!selectedNew) { setCompare(null); return; }
    let cancelled = false;
    setComparing(true);
    const q = new URLSearchParams();
    q.set("new", selectedNew);
    if (selectedOld) q.set("old", selectedOld);
    api.get(`/projects/${projectId}/revisions/compare?${q.toString()}`)
      .then((r) => { if (!cancelled) setCompare(r.data); })
      .catch((e) => {
        if (!cancelled) {
          setCompare(null);
          toast.error(e?.response?.data?.detail || "Compare failed");
        }
      })
      .finally(() => { if (!cancelled) setComparing(false); });
    return () => { cancelled = true; };
  }, [projectId, selectedOld, selectedNew]);

  const submitCreate = async () => {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      await api.post(`/projects/${projectId}/plan-sets`, {
        name: newName.trim(),
        notes: newNotes.trim() || null,
        copy_sheets_from_current: true,
        notify: true,
      });
      toast.success("Revision created and team notified");
      setShowCreate(false);
      setNewName(""); setNewNotes("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const resendNotify = async (planSetId) => {
    setNotifyingId(planSetId);
    try {
      await api.post(`/projects/${projectId}/revisions/notify`, {
        plan_set_id: planSetId, counts: {},
      });
      toast.success("Revision notice sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Notify failed");
    } finally { setNotifyingId(null); }
  };

  const counts = compare?.counts || { added: 0, removed: 0, changed: 0, renamed: 0 };

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="project-revisions-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to={`/project/${projectId}`} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">
          <ArrowLeft size={12} /> {project?.name || "Project"}
        </Link>
        {canWrite && (
          <button
            onClick={() => setShowCreate(true)}
            className="btn btn-primary inline-flex items-center gap-1.5 text-xs"
            data-testid="create-revision-btn"
          >
            <Plus size={12} /> New revision
          </button>
        )}
      </div>

      <div className="fop-surface-2 p-5 rounded-lg mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Intelligence · Revisions</div>
        <h1 className="font-display text-2xl text-[var(--fop-text)]">Revision History &amp; Compare</h1>
        <p className="text-xs text-[var(--fop-text-3)] mt-1 max-w-2xl">
          Every revision package issued on this project. Select any two plan sets to see exactly what was added, removed, changed, or renamed.
        </p>
      </div>

      {error && (
        <div className="fop-surface-2 p-4 rounded-lg mb-4 border border-red-500/40 text-sm text-red-300" data-testid="revisions-error">{error}</div>
      )}

      {loading ? (
        <div className="p-8 font-mono text-xs text-[var(--fop-text-3)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading revisions…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
          {/* Timeline */}
          <div className="fop-surface-2 rounded-lg p-4" data-testid="revisions-timeline">
            <div className="flex items-center justify-between mb-3">
              <div className="inline-flex items-center gap-2 text-[var(--fop-text-2)]">
                <History size={14} />
                <span className="text-xs font-mono uppercase tracking-wider">Timeline</span>
              </div>
              <span className="text-[10px] font-mono text-[var(--fop-text-4)]">{history.length} revision{history.length === 1 ? "" : "s"}</span>
            </div>
            {history.length === 0 ? (
              <div className="text-xs text-[var(--fop-text-3)] py-8 text-center">No revisions yet</div>
            ) : (
              <ul className="space-y-2">
                {history.map((rev) => {
                  const isNew = selectedNew === rev.id;
                  const isOld = selectedOld === rev.id;
                  return (
                    <li
                      key={rev.id}
                      data-testid={`revision-row-${rev.id}`}
                      className={
                        "rounded-md border p-3 transition-colors cursor-pointer " +
                        (isNew ? "border-[var(--fop-primary)] bg-[var(--fop-primary-soft)]" :
                         isOld ? "border-[var(--fop-border-hi)] bg-[var(--fop-surface)]" :
                         "border-[var(--fop-border)] hover:border-[var(--fop-border-hi)] bg-[var(--fop-surface)]")
                      }
                      onClick={() => {
                        if (selectedNew === rev.id) {
                          setSelectedOld(rev.id); setSelectedNew(null);
                        } else if (selectedOld === rev.id) {
                          setSelectedOld(null);
                        } else if (!selectedNew) {
                          setSelectedNew(rev.id);
                        } else {
                          setSelectedOld(selectedNew);
                          setSelectedNew(rev.id);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">
                              Rev {rev.revision_number}
                            </span>
                            {rev.is_current && (
                              <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded bg-[var(--fop-primary)] text-black">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="font-medium text-sm text-[var(--fop-text)] truncate">{rev.name || "Working Set"}</div>
                          <div className="text-[11px] text-[var(--fop-text-3)] mt-0.5">{fmtDate(rev.issued_date)}</div>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] font-mono text-[var(--fop-text-4)]">
                            <span>{rev.sheet_count} sheets</span>
                            {(rev.added > 0 || rev.removed > 0 || rev.changed > 0) && (
                              <>
                                <span className="text-emerald-400">+{rev.added}</span>
                                <span className="text-red-400">−{rev.removed}</span>
                                <span className="text-amber-400">~{rev.changed}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {(isNew || isOld) && (
                          <span className={"text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded " +
                            (isNew ? "bg-[var(--fop-primary)] text-black" : "border border-[var(--fop-border-hi)] text-[var(--fop-text-2)]")}>
                            {isNew ? "NEW" : "OLD"}
                          </span>
                        )}
                      </div>
                      {canWrite && (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); resendNotify(rev.id); }}
                            disabled={notifyingId === rev.id}
                            data-testid={`notify-revision-${rev.id}`}
                            className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {notifyingId === rev.id ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
                            Notify team
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Diff panel */}
          <div className="fop-surface-2 rounded-lg p-5" data-testid="compare-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="inline-flex items-center gap-2 text-[var(--fop-text-2)]">
                <GitCompareArrows size={14} />
                <span className="text-xs font-mono uppercase tracking-wider">Compare</span>
              </div>
              {comparing && <Loader2 size={14} className="animate-spin text-[var(--fop-text-3)]" />}
            </div>

            {!selectedNew ? (
              <div className="text-sm text-[var(--fop-text-3)] py-10 text-center">
                Click a revision on the left to compare.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 text-xs text-[var(--fop-text-2)] mb-4 flex-wrap">
                  <span className="font-mono uppercase tracking-wider text-[var(--fop-text-4)]">Old:</span>
                  <span className="font-medium">{compare?.old_plan_set_name || "—"}</span>
                  <span className="text-[var(--fop-text-4)]">→</span>
                  <span className="font-mono uppercase tracking-wider text-[var(--fop-text-4)]">New:</span>
                  <span className="font-medium">{compare?.new_plan_set_name || "—"}</span>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-4">
                  <Stat label="Added" value={counts.added} accent="emerald" />
                  <Stat label="Removed" value={counts.removed} accent="red" />
                  <Stat label="Changed" value={counts.changed} accent="amber" />
                  <Stat label="Renamed" value={counts.renamed} accent="blue" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DiffList title="Added" rows={compare?.added || []} icon={<PlusCircle size={12} className="text-emerald-400" />} />
                  <DiffList title="Removed" rows={compare?.removed || []} icon={<MinusCircle size={12} className="text-red-400" />} />
                  <DiffList title="Changed" rows={compare?.changed || []} icon={<AlertTriangle size={12} className="text-amber-400" />} />
                  <DiffList title="Renamed" rows={compare?.renamed || []} icon={<RefreshCw size={12} className="text-blue-400" />} />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !creating && setShowCreate(false)}>
          <div className="fop-surface-2 rounded-lg p-5 w-full max-w-md border border-[var(--fop-border-hi)]" onClick={(e) => e.stopPropagation()} data-testid="create-revision-modal">
            <h3 className="font-display text-lg text-[var(--fop-text)] mb-1">New Revision</h3>
            <p className="text-xs text-[var(--fop-text-3)] mb-4">Creates a new plan set. Existing sheets are carried forward. Team is notified automatically.</p>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Name</label>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder='e.g., "Addendum 3" or "100% IFC"'
              data-testid="new-revision-name"
              className="w-full px-3 py-2 mb-3 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm text-[var(--fop-text)]"
            />
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Notes (optional)</label>
            <textarea
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              rows={3}
              data-testid="new-revision-notes"
              className="w-full px-3 py-2 mb-4 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm text-[var(--fop-text)] resize-none"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setShowCreate(false)} disabled={creating} className="btn btn-ghost text-xs">Cancel</button>
              <button onClick={submitCreate} disabled={creating || !newName.trim()} data-testid="confirm-create-revision" className="btn btn-primary text-xs inline-flex items-center gap-1.5">
                {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create &amp; notify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const colour = {
    emerald: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
    red: "text-red-400 border-red-500/30 bg-red-500/5",
    amber: "text-amber-400 border-amber-500/30 bg-amber-500/5",
    blue: "text-blue-400 border-blue-500/30 bg-blue-500/5",
  }[accent] || "text-[var(--fop-text)]";
  return (
    <div className={`rounded-md border p-2.5 ${colour}`} data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-[9px] font-mono uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

function DiffList({ title, rows, icon }) {
  return (
    <div className="border border-[var(--fop-border)] rounded p-3" data-testid={`diff-list-${title.toLowerCase()}`}>
      <div className="inline-flex items-center gap-1.5 mb-2">
        {icon}
        <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{title} · {rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-[var(--fop-text-4)] py-2 italic">None</div>
      ) : (
        <ul className="space-y-1 max-h-56 overflow-y-auto">
          {rows.map((r, i) => (
            <li key={`${r.sheet_number}-${i}`} className="text-[11px] font-mono text-[var(--fop-text-2)] truncate">
              <span className="text-[var(--fop-text)]">{r.sheet_number}</span>
              {r.sheet_title && <span className="text-[var(--fop-text-3)]"> · {r.sheet_title}</span>}
              {r.old_revision && r.new_revision && r.old_revision !== r.new_revision && (
                <span className="text-[var(--fop-text-4)]"> · {r.old_revision} → {r.new_revision}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
